import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spinner, Table, Row, Col, Form } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';

// --- helpers ---
const toDate = (ts) =>
  ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);

const ageInDays = (d) => (d ? Math.floor((new Date() - d) / (1000 * 60 * 60 * 24)) : null);

// stock number fallback to last 8 VIN
const stockDisplay = (v) =>
  v?.strStockNbr
    ? v.strStockNbr
    : v?.strVin?.length >= 8
      ? v.strVin.slice(-8)
      : (v?.strVin || '');

const resolveModelName = (v, modelCodes = {}) => {
  const factory = (v?.strFactory ?? v?.strMake ?? '').trim();
  const factory2 = factory.toLowerCase().includes('ford') ? 'Ford' : factory.toLowerCase().includes('gm') ? 'GM' : factory;
  const model = (v?.strModel ?? '').trim();
  if (!factory && !model) return '';
  const k1 = `${factory2} - ${model}`;
  const k2 = `${factory2} - ${model.substring(0, 3)}`;
  return modelCodes[k1] || modelCodes[k2] || model || '';
};

// Recv Date like "6/9"
const fmtMonthDay = (d) => {
  if (!d) return '';
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${m}/${day}`;
};

const titleDate = () => {
  const d = new Date();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const yy = String(d.getFullYear()).slice(-2);
  return `${m}/${day}/${yy}`;
};

// --- component ---
export default function StockByModelReport() {
  const { onSnapshotQueryData, getData } = useFirestore();

  const [vehicles, setVehicles] = useState([]);
  const [modelCodes, setModelCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [factory, setFactory] = useState('Ford'); // dropdown: Ford | GM

  // live: only Stock vehicles
  useEffect(() => {
    const unsub = onSnapshotQueryData(
      'vehicles',
      'strSalesOrdNbr', '==', 'Stock',
      (data) => {
        setVehicles(data || []);
        setLoading(false);
      }
    );
    return () => unsub && unsub();
  }, [onSnapshotQueryData]);

  // model code names
  useEffect(() => {
    (async () => {
      try {
        const data = await getData('modelCodes');
        const map = (data || []).reduce((acc, obj) => {
          acc[`${obj.strFactory} - ${obj.strModel}`] = obj.strModelName;
          return acc;
        }, {});
        setModelCodes(map);
      } catch {/* ignore */ }
    })();
  }, [getData]);

  // grouped rows by model (friendly name); only vehicles with dtRecvDate
  const grouped = useMemo(() => {
    const out = {};
    const wantFord = factory.toLowerCase() === 'ford';

    (vehicles || []).forEach((v) => {
      const recv = toDate(v.dtRecvDate);
      if (!recv) return;

      // factory filter using strFactory
      const vf = (v.strFactory || '').toLowerCase();
      const isFord = vf.includes('ford');
      if (wantFord ? !isFord : isFord) return;

      const modelFriendly = resolveModelName(v, modelCodes) || '(Unknown Model)';

      const row = {
        id: v.id,
        stock: stockDisplay(v),
        vin: v.strVin || '',
        yr: v.strYR || '',
        make: (v.strMake || '').toUpperCase(),
        model: modelFriendly,
        modelCode: v.strModel || '',
        color: v.strColor || '',
        recv: recv,
        age: ageInDays(recv) ?? '',
      };

      if (!out[modelFriendly]) out[modelFriendly] = [];
      out[modelFriendly].push(row);
    });

    // sort groups alphabetically, and rows by oldest first
    Object.values(out).forEach((rows) => rows.sort((a, b) => (b.age ?? -1) - (a.age ?? -1)));

    return Object.fromEntries(
      Object.entries(out).sort(([a], [b]) => a.localeCompare(b))
    );
  }, [vehicles, modelCodes, factory]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Body>
        <div className="text-center mb-3">
          <div className="fw-bold">Hardy Fleet Group</div>
          <div>Inventory Age Report</div>
          <div className="fw-bold">{`Stock ${factory} by Model`}</div>
          <div className="fw-bold">{titleDate()}</div>
        </div>

        <Row className="mb-3">
          <Col xs={12} md="auto" className="ms-auto">
            <Form.Select
              value={factory}
              onChange={(e) => setFactory(e.target.value)}
              style={{ width: 200 }}
            >
              <option value="Ford">Ford</option>
              <option value="GM">GM</option>
            </Form.Select>
          </Col>
        </Row>

        <Table bordered responsive>
          <thead>
            <tr>
              <th>Stock #</th>
              <th>Vin</th>
              <th>Yr</th>
              <th>Make</th>
              <th>Model</th>
              <th>Model Code</th>
              <th>Color</th>
              <th>Recv Date</th>
              <th>Age</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([modelName, rows]) => (
              <React.Fragment key={modelName}>
                <tr>
                  <td colSpan={9} className="fw-bold pt-3">{modelName}</td>
                </tr>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td>{r.stock}</td>
                    <td>{r.vin}</td>
                    <td>{r.yr}</td>
                    <td>{r.make}</td>
                    <td>{r.model}</td>
                    <td>{r.modelCode}</td>
                    <td>{r.color}</td>
                    <td>{fmtMonthDay(r.recv)}</td>
                    <td>{r.age}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-muted">No vehicles found.</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {Object.keys(grouped).length === 0 && (
              <tr>
                <td colSpan={9} className="text-muted text-center">No vehicles to display.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}
